import { useRef, useState } from "react";
import {
  IBenefitRowResult,
  IBenefitUploadResponse,
  BenefitUploadStatus,
} from "../../interfaces/benefit";
import { uploadBenefitExcel } from "../../services/benefitService";
import { downloadBase64Excel } from "../../utils/download";
import { formatFileSize } from "../../utils/format";
import Modal from "../ui/Modal";
import {
  AlertIcon,
  DownloadIcon,
  FileIcon,
  InfoIcon,
  UploadIcon,
} from "../ui/Icons";

interface IUploadModalProps {
  user?: string;
  onClose: () => void;
  onUploaded: () => void;
}

const STATUS_CLASS: Record<BenefitUploadStatus, string> = {
  CARGADO: "result-row--loaded",
  ERROR: "result-row--error",
  DUPLICADO: "result-row--duplicated",
  "CARGA RECIENTE": "result-row--recent",
};

const STATUS_PILL: Record<BenefitUploadStatus, string> = {
  CARGADO: "pill pill--loaded",
  ERROR: "pill pill--error",
  DUPLICADO: "pill pill--duplicated",
  "CARGA RECIENTE": "pill pill--recent",
};

export default function UploadModal({ user, onClose, onUploaded }: IUploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IBenefitUploadResponse | null>(null);

  const handleSelectFile = (selected: File | undefined): void => {
    if (!selected) return;

    if (!/\.(xlsx|xls)$/i.test(selected.name)) {
      setError("El archivo debe ser un excel (.xlsx o .xls).");
      return;
    }

    setError(null);
    setFile(selected);
  };

  const handleUpload = async (): Promise<void> => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const response = await uploadBenefitExcel({ file, user });
      setResult(response);
      onUploaded();
    } catch (uploadError) {
      console.error("ERROR AL CARGAR NOMINA", uploadError);
      setError(
        "No pudimos procesar el archivo. Revisa que tenga las columnas requeridas e intenta nuevamente."
      );
    } finally {
      setIsUploading(false);
    }
  };

  // el excel coloreado solo se descarga cuando el usuario lo pide
  const handleDownloadResult = (): void => {
    if (!result?.excel) return;

    downloadBase64Excel({
      base64: result.excel.base64,
      fileName: result.excel.fileName,
    });
  };

  if (result) {
    const { summary } = result;

    return (
      <Modal
        title="Resultado de la carga"
        subtitle={summary.file}
        onClose={onClose}
        wide
        footer={
          <>
            <button type="button" className="btn" onClick={handleDownloadResult}>
              <DownloadIcon size={15} />
              Descargar excel de resultado
            </button>
            <div className="modal__foot-spacer" />
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Listo
            </button>
          </>
        }
      >
        <div className="summary">
          <div className="summary__item summary__item--total">
            <div className="summary__label">Total</div>
            <div className="summary__value">{summary.totalRecords}</div>
          </div>
          <div className="summary__item summary__item--ok">
            <div className="summary__label">Cargados</div>
            <div className="summary__value">{summary.loadedRecords}</div>
          </div>
          <div className="summary__item summary__item--recent">
            <div className="summary__label">Recientes</div>
            <div className="summary__value">{summary.recentRecords}</div>
          </div>
          <div className="summary__item summary__item--warn">
            <div className="summary__label">Duplicados</div>
            <div className="summary__value">{summary.duplicatedRecords}</div>
          </div>
          <div className="summary__item summary__item--err">
            <div className="summary__label">Con error</div>
            <div className="summary__value">{summary.failedRecords}</div>
          </div>
        </div>

        <div className="banner banner--info">
          <span className="banner__icon">
            <InfoIcon size={15} />
          </span>
          <span>
            El excel con los registros coloreados no se descargó automáticamente.
            Usa el botón del pie para obtenerlo.
          </span>
        </div>

        <div className="result-list">
          {summary.records.map((record: IBenefitRowResult, index: number) => (
            <div
              className={`result-row ${STATUS_CLASS[record.status]}`}
              key={`${record.row}-${index}`}
            >
              <span className="result-row__index">{record.row}</span>
              <span className="result-row__rut">{record.DNI || "—"}</span>
              <span className="result-row__detail">
                <span className="result-row__name">
                  {`${record.names} ${record.lastnames}`.trim() || "Sin nombre"}
                </span>
                {" · "}
                <span className="result-row__msg">{record.message}</span>
              </span>
              <span className={STATUS_PILL[record.status]}>{record.status}</span>
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Cargar nómina"
      subtitle="Archivo excel con RUT, nombres, apellidos y teléfono"
      onClose={isUploading ? undefined : onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancelar
          </button>
          <div className="modal__foot-spacer" />
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleUpload}
            disabled={!file || isUploading}
          >
            {isUploading ? <span className="btn__spin" /> : <UploadIcon size={15} />}
            {isUploading ? "Procesando…" : "Cargar nómina"}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner banner--err">
          <span className="banner__icon">
            <AlertIcon size={15} />
          </span>
          <span>{error}</span>
        </div>
      )}

      {!file ? (
        <div
          className={isDragOver ? "dropzone dropzone--over" : "dropzone"}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragOver(false);
            handleSelectFile(event.dataTransfer.files[0]);
          }}
        >
          <span className="dropzone__icon">
            <UploadIcon size={26} />
          </span>
          <span className="dropzone__title">
            Arrastra el excel aquí o haz clic para buscarlo
          </span>
          <span className="dropzone__hint">
            Se aceptan .xlsx y .xls. Las columnas pueden venir en cualquier orden.
          </span>
        </div>
      ) : (
        <div className="file-chip">
          <span className="file-chip__icon">
            <FileIcon size={16} />
          </span>
          <span className="file-chip__text">
            <span className="file-chip__name">{file.name}</span>
            <span className="file-chip__size">{formatFileSize(file.size)}</span>
          </span>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => setFile(null)}
            disabled={isUploading}
          >
            Cambiar
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        className="hidden-input"
        type="file"
        accept=".xlsx,.xls"
        onChange={(event) => handleSelectFile(event.target.files?.[0])}
      />
    </Modal>
  );
}
